import { useEffect } from 'react'

/**
 * Shared client-portal theming: turns a tenant's branding settings (primary/secondary colour,
 * header style, corner style, font) into CSS custom properties any portal-scoped component can
 * read. Replaces three previously-duplicated copies of the same contrast-calculation logic
 * (PortalLayout, PublicSessionsPage, EmbedSessionsPage).
 */

export type HeaderStyle = 'Dark' | 'Light'
export type CornerStyle = 'Rounded' | 'Sharp'
export type FontKey = 'System' | 'Inter' | 'Manrope' | 'SourceSerif4'

export interface BrandInput {
  primaryColour?: string | null
  secondaryColour?: string | null
  headerStyle?: string | null
  cornerStyle?: string | null
  fontFamily?: string | null
}

export const FONT_OPTIONS: Record<FontKey, { label: string; stack: string; googleFontParam: string | null }> = {
  System: { label: 'System default', stack: 'ui-sans-serif, system-ui, sans-serif', googleFontParam: null },
  Inter: { label: 'Inter (clean, modern)', stack: "'Inter', ui-sans-serif, system-ui, sans-serif", googleFontParam: 'Inter:wght@400;500;600;700' },
  Manrope: { label: 'Manrope (rounded, friendly)', stack: "'Manrope', ui-sans-serif, system-ui, sans-serif", googleFontParam: 'Manrope:wght@400;500;600;700;800' },
  SourceSerif4: { label: 'Source Serif (traditional)', stack: "'Source Serif 4', ui-serif, Georgia, serif", googleFontParam: 'Source+Serif+4:wght@400;600;700' },
}

const DEFAULT_PRIMARY = '#4F46E5'

/** Returns true when white text will be readable on the given hex background. */
export function isColourDark(hex: string): boolean {
  try {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    // Perceived luminance (ITU-R BT.601)
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55
  } catch {
    return true
  }
}

function fontKey(v?: string | null): FontKey {
  return v === 'Inter' || v === 'Manrope' || v === 'SourceSerif4' ? v : 'System'
}

/** Builds the CSS custom properties (+ font-family) to apply on a portal-scoped root element. */
export function buildBrandStyle(brand: BrandInput): React.CSSProperties {
  const primary = brand.primaryColour || DEFAULT_PRIMARY
  const secondary = brand.secondaryColour || primary
  const cornerStyle: CornerStyle = brand.cornerStyle === 'Sharp' ? 'Sharp' : 'Rounded'
  const font = FONT_OPTIONS[fontKey(brand.fontFamily)]

  const vars: Record<string, string> = {
    '--brand-primary': primary,
    '--brand-primary-contrast': isColourDark(primary) ? '#ffffff' : '#111111',
    '--brand-secondary': secondary,
    '--brand-secondary-contrast': isColourDark(secondary) ? '#ffffff' : '#111111',
    '--brand-radius': cornerStyle === 'Sharp' ? '4px' : '0.75rem',
    '--brand-radius-sm': cornerStyle === 'Sharp' ? '2px' : '0.5rem',
    '--brand-radius-full': cornerStyle === 'Sharp' ? '4px' : '9999px',
  }

  // shadcn/ui components (Card, Table, Input, Badge, Button) read Tailwind's own
  // --radius-* tokens, not the --brand-* ones above — override those too so "Sharp"
  // actually reaches cards/tables instead of just the buttons this file styles directly.
  if (cornerStyle === 'Sharp') {
    vars['--radius-sm'] = '2px'
    vars['--radius-md'] = '3px'
    vars['--radius-lg'] = '4px'
    vars['--radius-xl'] = '4px'
    vars['--radius-2xl'] = '6px'
  }

  return { ...vars, fontFamily: font.stack } as React.CSSProperties
}

export function isHeaderLight(brand: BrandInput): boolean {
  return brand.headerStyle === 'Light'
}

/** Injects (once) a <link> tag for the chosen Google Font, if it isn't the system default. */
export function useBrandFont(fontFamily?: string | null) {
  const key = fontKey(fontFamily)
  useEffect(() => {
    const option = FONT_OPTIONS[key]
    if (!option.googleFontParam) return

    const id = `brand-font-${key}`
    if (document.getElementById(id)) return

    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${option.googleFontParam}&display=swap`
    document.head.appendChild(link)
  }, [key])
}
